import type {
  ConsolidationTheme,
  DiscussionArcPoint,
  PanelRecapTriggerKind,
  RecapSection,
} from "../types.ts";
import {
  PANEL_RECAP_CONSOLIDATION_KEY,
  getPromptContract,
} from "../promptContracts/registry.ts";
import {
  MAX_RECAP_CONSOLIDATION_ARC_SUBPOINTS,
  MAX_RECAP_CONSOLIDATION_NARRATIVE_ITEMS,
} from "./limits.ts";
import {
  normalizePanelRecapSectionResponse,
} from "./schema.ts";

export const SYSTEM_PROMPT = getPromptContract(PANEL_RECAP_CONSOLIDATION_KEY).systemDefault;

export const PRIMARY_CONSOLIDATION_MIN_MS = 5 * 60_000;
export const PRIMARY_CONSOLIDATION_MIN_NEW_SECTIONS = 1;
export const CONSOLIDATION_QUIET_PERIOD_MS = 60_000;
export const EPISODE_CONSOLIDATION_COALESCE_MS = 5_000;
export const EPISODE_CONSOLIDATION_MIN_SPACING_MS = 60_000;
export const EPISODE_CONSOLIDATION_FAST_SPACING_MS = 60_000;

export type ConsolidationDecisionReason =
  | "primary"
  | "already_generating"
  | "no_latest_recap"
  | "no_new_sections"
  | "not_enough_new_sections"
  | "too_soon";

export type ConsolidationMode = "bootstrap" | "incremental" | "regenerate";

export interface RecapConsolidationCoverageManifestItem {
  windowId: string;
  windowOrdinal: number;
  sourceStartTs: number;
  sourceEndTs: number;
  inputFingerprint: string;
  outcome: "generated" | "non_substantive";
  artifactCount: number;
}

export interface RecapConsolidationClaimOperation {
  sourceEpisodeOrdinal?: number;
  kind: "new" | "extends" | "repeat";
  speaker: string;
  text: string;
  subpoints: string[];
}

export function validateConsolidationCoverageManifest(
  items: RecapConsolidationCoverageManifestItem[],
  afterWindowOrdinal: number,
  throughWindowOrdinal: number,
): { ok: true } | { ok: false; reason: string } {
  let expected = afterWindowOrdinal + 1;
  for (const item of items) {
    if (item.windowOrdinal !== expected) {
      return {
        ok: false,
        reason: `coverage_gap_expected_${expected}_found_${item.windowOrdinal}`,
      };
    }
    expected += 1;
  }
  if (expected - 1 !== throughWindowOrdinal) {
    return { ok: false, reason: `coverage_gap_expected_through_${throughWindowOrdinal}` };
  }
  return { ok: true };
}

export interface ConsolidationSectionBatch {
  startSectionOrdinal: number;
  endSectionOrdinalExclusive: number;
  estimatedChars: number;
}

export function planConsolidationSectionBatches(
  sectionCosts: number[],
  maxBatchChars: number,
): ConsolidationSectionBatch[] {
  if (!Number.isSafeInteger(maxBatchChars) || maxBatchChars <= 0) {
    throw new Error("Consolidation batch budget must be a positive safe integer.");
  }
  const batches: ConsolidationSectionBatch[] = [];
  let start = 0;
  while (start < sectionCosts.length) {
    let end = start;
    let estimatedChars = 0;
    while (end < sectionCosts.length) {
      const cost = Math.max(1, Math.ceil(sectionCosts[end]));
      if (end > start && estimatedChars + cost > maxBatchChars) break;
      estimatedChars += cost;
      end += 1;
      if (estimatedChars >= maxBatchChars) break;
    }
    batches.push({
      startSectionOrdinal: start,
      endSectionOrdinalExclusive: end,
      estimatedChars,
    });
    start = end;
  }
  return batches;
}

export function isConsolidationRunClaimable(
  run: {
    status: "pending" | "generating" | "retryable_error" | "ready" | "published" | "superseded" | "blocked";
    nextRetryAt?: number;
    leaseExpiresAt?: number;
  },
  now: number,
): boolean {
  if (run.status === "pending") return true;
  if (run.status === "retryable_error") return (run.nextRetryAt ?? 0) <= now;
  if (run.status === "generating" || run.status === "ready") {
    return (run.leaseExpiresAt ?? 0) <= now;
  }
  return false;
}

export function consolidationPublicationDecision(input: {
  runBaseConsolidationId: string | null;
  runBaseVersion: number;
  runTargetWindowOrdinal: number;
  runAttributionRevision?: number;
  currentConsolidationId: string | null;
  currentVersion: number;
  currentThroughWindowOrdinal: number;
  currentAttributionRevision?: number;
}): "publish" | "superseded" {
  return input.runBaseConsolidationId === input.currentConsolidationId &&
      input.runBaseVersion === input.currentVersion &&
      (input.runAttributionRevision ?? 0) === (input.currentAttributionRevision ?? 0) &&
      input.runTargetWindowOrdinal >= input.currentThroughWindowOrdinal
    ? "publish"
    : "superseded";
}

export interface ConsolidationDecision {
  shouldConsolidate: boolean;
  reason: ConsolidationDecisionReason;
}

export type QuietConsolidationDecisionReason =
  | "quiet_period"
  | "already_generating"
  | "no_latest_recap"
  | "latest_recap_moved"
  | "already_consolidated"
  | "no_new_sections";

export interface QuietConsolidationDecision {
  shouldConsolidate: boolean;
  reason: QuietConsolidationDecisionReason;
}

export type QuietSweepWorkDisposition = "run" | "clear" | "reschedule";

export function quietSweepDecisionDisposition(
  reason: QuietConsolidationDecisionReason,
): QuietSweepWorkDisposition {
  if (reason === "quiet_period") return "run";
  if (reason === "already_generating" || reason === "latest_recap_moved") {
    return "reschedule";
  }
  return "clear";
}

export function quietSweepRunDisposition(
  reason: string | undefined,
  created: boolean,
): "clear" | "retain" | "reschedule" {
  if (created || reason === "published" || reason === "already_consolidated") return "clear";
  if (
    reason === "consolidation_batch_pending" ||
    reason === "consolidation_retry_not_due" ||
    reason === "consolidation_retry_scheduled" ||
    reason === "consolidation_blocked"
  ) {
    return "retain";
  }
  if (reason === "no_sections" || reason === "quiet_already_consolidated") return "clear";
  return "reschedule";
}

export interface RecapConsolidationTriggerInput {
  now: number;
  referenceTs: number;
  latestRecapTriggerKind: PanelRecapTriggerKind | null;
  newSectionCount: number;
  alreadyGenerating?: boolean;
}

export interface RecapConsolidationPromptContext {
  mode: ConsolidationMode;
  sessionTitle: string;
  purpose: string;
  themes: string[];
  panelists: Array<{ name: string; bio: string }>;
  throughTs: number;
  sections: RecapSection[];
  coverageManifest: RecapConsolidationCoverageManifestItem[];
  claimOperations?: RecapConsolidationClaimOperation[];
  previousConsolidation?: {
    narrative: DiscussionArcPoint[];
    themes: ConsolidationTheme[];
  };
  correctionInstruction?: string;
}

type PromptConsolidationTheme = Pick<
  ConsolidationTheme,
  | "title"
  | "synthesis"
  | "speakers"
  | "tensions"
  | "themeId"
  | "mergedFromThemeIds"
  | "projectorSummary"
>;

function projectPreviousConsolidationForPrompt(
  previous: NonNullable<RecapConsolidationPromptContext["previousConsolidation"]>,
): { narrative: DiscussionArcPoint[]; themes: PromptConsolidationTheme[] } {
  return {
    narrative: previous.narrative,
    themes: previous.themes.map((theme) => ({
      title: theme.title,
      synthesis: theme.synthesis,
      speakers: theme.speakers,
      tensions: theme.tensions,
      ...(theme.themeId === undefined ? {} : { themeId: theme.themeId }),
      ...(theme.mergedFromThemeIds === undefined
        ? {}
        : { mergedFromThemeIds: theme.mergedFromThemeIds }),
      ...(theme.projectorSummary === undefined
        ? {}
        : { projectorSummary: theme.projectorSummary }),
    })),
  };
}

export function shouldConsolidateEpisodes(input: {
  now: number;
  referenceTs: number;
  previousThroughEpisodeOrdinal: number;
  latestTerminalEpisodeOrdinal: number;
  spacingMs: number;
  alreadyGenerating?: boolean;
}): ConsolidationDecision {
  if (input.alreadyGenerating) {
    return { shouldConsolidate: false, reason: "already_generating" };
  }
  if (input.now - input.referenceTs < input.spacingMs) {
    return { shouldConsolidate: false, reason: "too_soon" };
  }
  if (input.latestTerminalEpisodeOrdinal <= input.previousThroughEpisodeOrdinal) {
    return { shouldConsolidate: false, reason: "no_new_sections" };
  }
  return { shouldConsolidate: true, reason: "primary" };
}

export function shouldConsolidate(input: RecapConsolidationTriggerInput): ConsolidationDecision {
  if (input.alreadyGenerating) {
    return { shouldConsolidate: false, reason: "already_generating" };
  }
  if (!input.latestRecapTriggerKind) {
    return { shouldConsolidate: false, reason: "no_latest_recap" };
  }
  if (input.newSectionCount < PRIMARY_CONSOLIDATION_MIN_NEW_SECTIONS) {
    return { shouldConsolidate: false, reason: "not_enough_new_sections" };
  }
  if (input.now - input.referenceTs < PRIMARY_CONSOLIDATION_MIN_MS) {
    return { shouldConsolidate: false, reason: "too_soon" };
  }
  return { shouldConsolidate: true, reason: "primary" };
}

export function shouldRunQuietConsolidation(input: {
  expectedLatestRecapSourceEndTs: number;
  latestRecapSourceEndTs: number | null;
  latestConsolidationThroughTs: number | null;
  newSectionCount: number;
  alreadyGenerating?: boolean;
}): QuietConsolidationDecision {
  if (input.alreadyGenerating) {
    return { shouldConsolidate: false, reason: "already_generating" };
  }
  if (input.latestRecapSourceEndTs === null) {
    return { shouldConsolidate: false, reason: "no_latest_recap" };
  }
  if (input.latestRecapSourceEndTs !== input.expectedLatestRecapSourceEndTs) {
    return { shouldConsolidate: false, reason: "latest_recap_moved" };
  }
  if (
    input.latestConsolidationThroughTs !== null &&
    input.latestConsolidationThroughTs >= input.latestRecapSourceEndTs
  ) {
    return { shouldConsolidate: false, reason: "already_consolidated" };
  }
  if (input.newSectionCount <= 0) {
    return { shouldConsolidate: false, reason: "no_new_sections" };
  }
  return { shouldConsolidate: true, reason: "quiet_period" };
}

export function buildRecapConsolidationUserPrompt(ctx: RecapConsolidationPromptContext): string {
  const previousBlock = ctx.previousConsolidation
    ? [
        "",
        "Previous consolidation output:",
        JSON.stringify(projectPreviousConsolidationForPrompt(ctx.previousConsolidation), null, 2),
      ]
    : [];
  const sectionLabel =
    ctx.mode === "incremental"
      ? "New A-tier recap sections since last consolidation:"
      : "A-tier recap sections:";
  const claimOperationsBlock = ctx.claimOperations?.length
    ? [
        "Canonical claim-ledger delta since the previous consolidation. Incorporate every claim and supporting detail into both the arc and themes:",
        ...ctx.claimOperations.flatMap((operation) => [
          `- ${operation.kind} ${operation.speaker}: ${operation.text}`,
          ...operation.subpoints.map((subpoint) => `  - ${subpoint}`),
        ]),
        "",
      ]
    : [];
  return [
    "Session:",
    ctx.sessionTitle || "(untitled)",
    "",
    "Session purpose:",
    ctx.purpose || "(not provided)",
    "",
    "Intended theme coverage:",
    ctx.themes.length ? ctx.themes.map((theme) => `- ${theme}`).join("\n") : "(not provided)",
    "",
    "Speaker bios / roles:",
    ctx.panelists.length
      ? ctx.panelists.map((panelist) => `- ${panelist.name}: ${panelist.bio}`).join("\n")
      : "(not provided)",
    "",
    `Consolidate through ts: ${ctx.throughTs}`,
    "",
    ...previousBlock,
    sectionLabel,
    ctx.sections.length ? formatSections(ctx.sections) : "(none)",
    "",
    ...claimOperationsBlock,
    "Coverage manifest (ordered source windows):",
    ctx.coverageManifest.length ? formatCoverageManifest(ctx.coverageManifest) : "(legacy/unavailable)",
    "",
    modeInstruction(ctx.mode),
    ...(ctx.correctionInstruction ? ["", ctx.correctionInstruction] : []),
  ].join("\n");
}

export function buildExtractiveRecapConsolidation(input: {
  sections: RecapSection[];
}): { narrative: DiscussionArcPoint[]; themes: ConsolidationTheme[] } {
  const seenPointText = new Set<string>();
  const points = normalizePanelRecapSectionResponse({ sections: input.sections }).sections
    .flatMap((section) =>
      section.points
        .filter((point) => {
          const key = `${section.speaker}:${point.text}`.toLowerCase();
          if (seenPointText.has(key)) return false;
          seenPointText.add(key);
          return true;
        })
        .map((point) => ({
          point: `${section.speaker}: ${point.text}`,
          subpoints: point.subpoints.slice(0, MAX_RECAP_CONSOLIDATION_ARC_SUBPOINTS),
        })),
    );

  return {
    narrative:
      points.length > 0
        ? points.slice(0, MAX_RECAP_CONSOLIDATION_NARRATIVE_ITEMS)
        : [{ point: "No substantive recap content was available.", subpoints: [] }],
    themes:
      points.length > 0
        ? [
            {
              title: "Discussion",
              synthesis: points.slice(0, 8).map((point) => point.point).join(" "),
              speakers: speakerNames(input.sections).slice(0, 8),
              tensions: [],
            },
          ]
        : [],
  };
}

export function buildRecapConsolidationFingerprint(input: {
  throughTs: number;
  sections: RecapSection[];
  coverageManifest?: RecapConsolidationCoverageManifestItem[];
  claimOperations?: RecapConsolidationClaimOperation[];
}): string {
  const payload = JSON.stringify({
    throughTs: input.throughTs,
    sections: input.sections.map((section) => ({
      timeTs: section.timeTs,
      speaker: section.speaker,
      speakerKey: section.speakerKey ?? null,
      points: section.points.map((point) => ({
        text: point.text,
        subpoints: point.subpoints,
      })),
    })),
    coverageManifest: input.coverageManifest ?? [],
    claimOperations: input.claimOperations ?? [],
  });
  return fnv1a(payload);
}

export function formatSections(sections: RecapSection[]): string {
  return sections
    .map((section) =>
      [
        `[${section.timeLabel} | ts=${section.timeTs}${
          section.speakerKey ? ` | speakerKey=${section.speakerKey}` : ""
        }] ${section.speaker}`,
        ...section.points.flatMap((point) => [
          `- ${point.text}`,
          ...point.subpoints.map((subpoint) => `  - ${subpoint}`),
        ]),
      ].join("\n"),
    )
    .join("\n\n");
}

function formatCoverageManifest(items: RecapConsolidationCoverageManifestItem[]): string {
  return items
    .map((item) =>
      [
        `#${item.windowOrdinal}`,
        item.windowId,
        `${item.sourceStartTs}-${item.sourceEndTs}`,
        item.outcome,
        `artifacts=${item.artifactCount}`,
        `fingerprint=${item.inputFingerprint}`,
      ].join(" | "),
    )
    .join("\n");
}

function modeInstruction(mode: ConsolidationMode): string {
  switch (mode) {
    case "bootstrap":
      return "Produce the initial consolidated summary. Return JSON with structured narrative objects and themes arrays only.";
    case "incremental":
      return [
        "Update the previous consolidation with the new material.",
        "This consolidation is also the session's final summary — it should read as one coherent arc, not an append log.",
        "Preserve the substance and chronological position of existing arc items, but do not treat their exact wording as permanently settled.",
        "Make focused wording changes when the growing arc has become repetitive, overly long, mechanically attributed, or chronologically awkward.",
        "If the new material continues the current phase, update that item's point or subpoints; if it opens a genuinely new phase, append a new item.",
        "New sections usually deepen an existing phase rather than open a new one; prefer enriching or merging over appending, so the arc stays segmented by topic instead of by speaker turn.",
        "Merge adjacent items only when they describe the same phase and their important details can be retained in subpoints.",
        "After incorporating the new material, read the full arc from beginning to end and make the minimum edits needed for natural continuity, varied sentence construction, and removal of stale positional language such as a conclusion followed by later developments.",
        "Enrich existing themes with new points and add a new theme only when the new material introduces a distinct topic.",
        "Preserve existing theme titles and structure unless the new material genuinely changes the picture.",
        "Return JSON with the full updated structured narrative objects and themes arrays only.",
      ].join(" ");
    case "regenerate":
      return [
        "The underlying recap sections have been corrected (speaker reassignment, text edits, or both).",
        "Your previous consolidation is shown above. It may now contain inaccurate claims, wrong speaker attributions, or stale phrasing.",
        "",
        "Update the consolidation to reflect the corrected material. Follow these structural constraints strictly:",
        "",
        "1. KEEP existing theme titles. Only rename a theme if the corrected evidence makes the current title factually wrong — not just because you'd phrase it differently.",
        "2. KEEP the current theme order. Only reorder if corrected evidence clearly makes the current ordering misleading (e.g., a theme attributed to the wrong part of the discussion).",
        "3. KEEP the current theme count. Only split a theme if it now covers two genuinely unrelated topics after corrections. Only merge two themes if corrections revealed they were about the same thing. Do not split or merge for stylistic reasons.",
        "4. REVISE each theme's synthesis paragraph to reflect corrected speaker attributions, corrected claims, and any changed relationships between speakers' positions. This is where most of your work should happen.",
        "5. UPDATE the narrative arc items where corrections change the shape of the discussion — a speaker turn that was misattributed, a claim that was wrong, or a shift that didn't happen. You may also make focused wording changes needed for the corrected full arc to read naturally without repetitive construction.",
        "6. UPDATE speaker lists and inline tensions to reflect corrected attributions.",
        "",
        "This is a correction pass. The audience has already read the previous version and built a mental model around its structure. Structural drift that isn't driven by factual corrections is a bug, not an improvement.",
        "",
        "Return JSON with the full updated structured narrative objects and themes arrays.",
      ].join("\n");
  }
}

function speakerNames(sections: RecapSection[]): string[] {
  const seen = new Set<string>();
  const speakers: string[] = [];
  for (const section of sections) {
    const speaker = section.speaker.trim();
    if (!speaker || seen.has(speaker.toLowerCase())) continue;
    seen.add(speaker.toLowerCase());
    speakers.push(speaker);
  }
  return speakers;
}

function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
